import { TherapistDto } from '../../../../../shared/dtos/therapist.dto';
import { UserProfileUpdateRequest } from '../../../../../shared/requests/userProfileUpdate.request';
import { FC, ReactElement, useMemo, useState } from 'react';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { Button, Form, InputGroup, Spinner, Stack } from 'react-bootstrap';
import * as Yup from 'yup';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import i18next from 'i18next';

type FormValues = {
  isCallAllowed: boolean;
  isChatAllowed: boolean;
  street: string;
  postal: string;
  city: string;
  country: string;
  telephone: string;
}

type MyAccountOverviewProps = {
  userProfile: TherapistDto;
  onSubmitSuccess?: (updated: TherapistDto) => void;
  onSubmitError?: (error: unknown) => void;
}

const MyAccountOverview: FC<MyAccountOverviewProps> = ({ userProfile, onSubmitSuccess, onSubmitError }: MyAccountOverviewProps): ReactElement => {

  const { authFetch } = useAuthFetchFromRestAPI();

  const [isSaving, setIsSaving] = useState(false);

  const dateOfBirthParts = useMemo(() => {
    const date: Date | null = userProfile.birthDate ? new Date(userProfile.birthDate) : null;

    return date
      ? {
        day: date.getDate().toString().padStart(2, '0'),
        month: (date.getMonth() + 1).toString().padStart(2, '0'),
        year: date.getFullYear().toString()
      }
      : { day: '', month: '', year: '' };
  }, [userProfile?.birthDate]);

  const initialValues: FormValues = useMemo(() => ({
    isCallAllowed: userProfile.isCallAllowed,
    isChatAllowed: !(userProfile.notAllowedFeature?.includes('CHAT')),
    street: userProfile.street ?? '',
    postal: userProfile.postal ?? '',
    city: userProfile.city ?? '',
    country: userProfile.country ?? '',
    telephone: userProfile.telephone ?? '',
  }), [userProfile]);

  const validationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
    isCallAllowed: Yup.boolean().required(),
    isChatAllowed: Yup.boolean().required(),
    street: Yup.string().trim().required(i18next.t('generic.required')),
    postal: Yup.string().trim().required(i18next.t('generic.required')),
    city: Yup.string().trim().required(i18next.t('generic.required')),
    country: Yup.string().trim().required(i18next.t('generic.required')),
    telephone: Yup.string().trim().required(i18next.t('generic.required')),
  });

  const buildPayload = (values: FormValues): UserProfileUpdateRequest => {
    const notAllowedFeature: string[] = values.isChatAllowed ? [] : ['CHAT'];
    return {
      isCallAllowed: values.isCallAllowed,
      notAllowedFeature,
      street: values.street.trim(),
      postal: values.postal.trim(),
      city: values.city.trim(),
      country: values.country.trim(),
      telephone: values.telephone.trim(),
    };
  };

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>): Promise<void> => {
    setIsSaving(true);
    setSubmitting(true);

    try {
      const payload = buildPayload(values);
      const updated: TherapistDto = await authFetch('/api/v1/user-profile', { method: 'POST', body: payload } );
      onSubmitSuccess?.(updated);
    } catch (error) {
      console.error(error);
      onSubmitError?.(error);
    } finally {
      setIsSaving(false);
      setSubmitting(false);
    }
  };

  const handleSwitch = (callback: () => void, submit: () => void): void => {
    callback();
    submit();
  };


  return (
    <div className="position-relative pt-3 w-50">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255, 255, 255, 0.6)', zIndex: 2 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}>
        {({ values, touched, errors, handleSubmit, handleChange, handleBlur, setFieldValue, isSubmitting } : FormikProps<FormValues>) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            className="p-2"
            style={{ opacity: isSaving ? 0.5 : 1.0 }}>
            <Stack gap={4}>
              <section>
                <h2 className="fw-semibold mb-3">{i18next.t('myAccountOverview.patientContactOptionHeader')}</h2>
                <p className="text-muted">{i18next.t('myAccountOverview.patientContactOptionBody')}</p>

                <Stack gap={2}>
                  <Form.Group controlId="isCallAllowed">
                    <Form.Check
                      type="switch"
                      name="isCallAllowed"
                      label={i18next.t('myAccountOverview.isCallAllowed')}
                      checked={values.isCallAllowed}
                      onChange={(event) => handleSwitch(
                        () => setFieldValue('isCallAllowed', event.currentTarget.checked),
                        () => handleSubmit()
                      )}
                    />
                  </Form.Group>

                  <Form.Group controlId="isChatAllowed">
                    <Form.Check
                      type="switch"
                      name="isChatAllowed"
                      label={i18next.t('myAccountOverview.isChatAllowed')}
                      checked={values.isChatAllowed}
                      onChange={(event) => handleSwitch(
                        () => setFieldValue('isChatAllowed', event.currentTarget.checked),
                        () => handleSubmit()
                      )}
                    />
                  </Form.Group>
                </Stack>
              </section>

              <section>
                <h2 className="fw-semibold mb-3">{i18next.t('myAccountOverview.yourAccountData')}</h2>
                <Stack gap={3}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group controlId="firstName">
                        <Form.Label>{i18next.t('accountSetup.firstName')}</Form.Label>
                        <Form.Control type="text" value={userProfile.firstName ?? ''} disabled />
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group controlId="lastName">
                        <Form.Label>{i18next.t('accountSetup.lastName')}</Form.Label>
                        <Form.Control type="text" value={userProfile.lastName ?? ''} disabled />
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group controlId="street">
                    <Form.Label>{i18next.t('accountSetup.street')}</Form.Label>
                    <Form.Control
                      type="text"
                      name="street"
                      value={values.street}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.street && !!errors.street}
                    />
                    <Form.Control.Feedback type="invalid">{errors.street}</Form.Control.Feedback>
                  </Form.Group>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group controlId="postal">
                        <Form.Label>{i18next.t('accountSetup.postal')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="postal"
                          value={values.postal}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.postal && !!errors.postal}
                        />
                        <Form.Control.Feedback type="invalid">{errors.postal}</Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group controlId="city">
                        <Form.Label>{i18next.t('accountSetup.city')}</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={values.city}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.city && !!errors.city}
                        />
                        <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group controlId="telephone">
                        <Form.Label>{i18next.t('accountSetup.telephone')}</Form.Label>
                        <Form.Control
                          type="tel"
                          name="telephone"
                          value={values.telephone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.telephone && !!errors.telephone}
                        />
                        <Form.Control.Feedback type="invalid">{errors.telephone}</Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group controlId="country">
                        <Form.Label>{i18next.t('accountSetup.country')}</Form.Label>
                        <Form.Select
                          name="country"
                          className="select-xl"
                          value={values.country}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.country && !!errors.country}>
                          {!values.country && <option value="">{i18next.t('accountSetup.country')}</option>}
                          {i18next.t('accountSetup.countryList').split(',').map((country: string) => {
                            const value: string = country.trim();
                            return (
                              <option key={value} value={value}>{value}</option>
                            );
                          })}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group controlId="dateOfBirth">
                    <Form.Label>{i18next.t('accountSetup.dateOfBirth')}</Form.Label>
                    <InputGroup className="gap-2 d-flex flex-row">
                      <Form.Control placeholder="DD" type="text" value={dateOfBirthParts.day} disabled style={{ maxWidth: 80 }} />
                      <Form.Control placeholder="MM" type="text" value={dateOfBirthParts.month} disabled style={{ maxWidth: 80 }} />
                      <Form.Control placeholder="YYYY" type="text" value={dateOfBirthParts.year} disabled style={{ maxWidth: 110 }} />
                    </InputGroup>
                  </Form.Group>

                  <div className="d-flex justify-content-start">
                    <Button variant="primary" type="submit" disabled={isSubmitting || isSaving}>
                      {i18next.t('generic.save')}
                    </Button>
                  </div>
                </Stack>
              </section>
            </Stack>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default MyAccountOverview;
